import { html, css, nothing } from 'lit';
import WarpElement from '@warp-ds/elements-core';
import { i18n } from '@lingui/core';
import activateI18n from './i18n.js';
import '@warp-ds/elements/components/button';
import {
    trackButtonClick,
    trackButtonClickUserMustVerify,
} from './tracking.js';
import './MessagingEidModal.js';

class FinnContactButton extends WarpElement {
    static styles = [
        WarpElement.styles,
        css`
            @unocss-placeholder;
        `,
    ];

    static get properties() {
        return {
            buttonText: { type: String },
            buttonVariant: { type: String, reflect: true },
            size: { type: String },
            trackingObjectName: { type: String },
            vertical: { type: String },
            trackingCategory: { type: String },
            title: { type: String },
            type: { type: String },
            isAdOwner: { type: Boolean },
            loginId: { type: Number },
            preview: { type: Boolean },
            requireEid: { type: Boolean },
            showError: { type: Boolean },
            locale: { type: String },
            url: { type: String },
            adId: { type: String },
            name: { type: String },
            unknownError: { type: Boolean },
            brand: { type: String },
            _eidModalOpen: { type: Boolean, state: true },
        };
    }

    constructor() {
        super();
        this.buttonVariant = 'primary';
        this.size = '';
        this.trackingObjectName = '';
        this.adId = '';
        this.vertical = '';
        this.trackingCategory = '';
        this.title = '';
        this.type = '';
        this.isAdOwner = false;
        this.loginId = '';
        this.preview = false;
        this.requireEid = false;
        this.showError = false;
        this.unknownError = false;
        this.brand = 'FINN';
        this.locale = '';
        this._eidModalOpen = false;
    }

    get isSmall() {
        return this.size.toLowerCase() === 'small';
    }

    connectedCallback() {
        super.connectedCallback();
        const element = document.getElementById('contact-button-data');
        try {
            const data = JSON.parse(element.textContent);
            this.url = data.url;
            this.adId = data.adId;
            this.vertical = data.vertical;
            this.trackingCategory = data.trackingCategory || 'n/a';
            this.title = data.title || 'n/a';
            this.type = data.type;
            this.isAdOwner = data.isAdOwner;
            this.loginId = data.loginId;
            this.requireEid = data.requireEid;
            this.name = data.name;
            this.preview = data.preview;
            this.showError = data.showError;
            this.unknownError = data.unknownError;
            this.brand = data.brand;
            this.locale = data.locale;
        } catch (error) { // eslint-disable-line
            // if data is not provided it will be supplied via component attributes
        }

        try {
            activateI18n(this.locale);
        } catch (err) {
            // eslint-disable-next-line no-console
            console.error('contact-button-podlet: unable to load locale.', err);
            this.unknownError = true;
        }
    }

    firstUpdated() {
        try {
            if ((this.loginId && this.isAdOwner) || this.unknownError) {
                this.showError = JSON.parse(
                    window.localStorage.getItem('showError'),
                );
                window.localStorage.removeItem('showError');
            } else {
                this.showError = false;
                window.localStorage.removeItem('showError');
            }
        } catch (err) {
            // eslint-disable-next-line no-console
            console.error(
                'contact-button-podlet: unable to set values from localStorage.',
                err,
            );
            this.unknownError = true;
        }
    }

    getButtonText() {
        return this.buttonText !== undefined
            ? this.buttonText
            : i18n.t({
                  id: 'contact-button.button-text.default',
                  message: 'Send message',
                  comment: 'Button text when not provided by user',
              });
    }

    buttonTemplate() {
        const errorText = i18n.t({
            id: 'contact-button.template.title',
            message: 'An unknown error occurred',
            comment: 'Title/popover used when unknown error occurs',
        });

        return html`
            <w-button
                variant="${this.buttonVariant.toLowerCase()}"
                title=${this.unknownError ? errorText : ''}
                @click="${this.errorCheck}"
                full-width
                ?small="${this.isSmall}"
            >
                ${this.getButtonText()}
            </w-button>
        `;
    }

    previewTemplate() {
        return html`
            <w-button
                title="preview button"
                variant="${this.buttonVariant.toLowerCase()}"
                @click="${this.openPreviewModal}"
                full-width
                ?small="${this.isSmall}"
            >
                ${this.getButtonText()}
            </w-button>
        `;
    }

    linkTemplate() {
        if (this.requireEid) {
            return html`
                <w-button
                    variant="${this.buttonVariant.toLowerCase()}"
                    @click="${this._openVerificationModal}"
                    full-width
                    ?small="${this.isSmall}"
                >
                    ${this.getButtonText()}
                </w-button>
                <messaging-eid-modal
                    .open="${this._eidModalOpen}"
                    .eidVerificationUrl="${this.url}"
                    .adId="${this.adId}"
                    .trackingCategory="${this.trackingCategory}"
                    .title="${this.title}"
                    .vertical="${this.vertical}"
                    .locale="${this.locale}"
                    .brand="${this.brand}"
                    @modal-hidden="${this._handleModalClose}"
                ></messaging-eid-modal>
            `;
        }

        return html`
            <w-button
                rel="nofollow"
                href="${this.url}"
                variant="${this.buttonVariant.toLowerCase()}"
                @click="${this.trackingSendMsg}"
                full-width
                ?small="${this.isSmall}"
            >
                ${this.getButtonText()}
            </w-button>
        `;
    }

    errorTemplate() {
        if (this.showError && this.isAdOwner) {
            return html`
                <p
                    class="s-text-negative text-xs text-center mt-8"
                    id="send-message-own-ad-error-msg"
                >
                    ${i18n.t({
                        id: 'contact-button.error.is-ad-owner',
                        message:
                            "Whoops! You can't send a message to yourself.",
                        comment:
                            'Text shown when user is ad owner, after button',
                    })}
                </p>
            `;
        }

        if (this.showError) {
            return html`
                <p
                    class="s-text-negative text-xs text-center mt-8"
                    id="send-message-own-ad-error-msg"
                >
                    ${i18n.t({
                        id: 'contact-button.error.unknown-error',
                        message: 'Oh no! Something went wrong.',
                        comment: 'Text shown when unknown error, after button',
                    })}
                </p>
            `;
        }

        return nothing;
    }

    getFinalTemplate() {
        if (this.preview) {
            return this.previewTemplate();
        }
        if (this.isAdOwner || this.unknownError) {
            return html`${this.buttonTemplate()}${this.errorTemplate()}`;
        }
        return this.linkTemplate();
    }

    render() {
        return html`<div>${this.getFinalTemplate()}</div>`;
    }

    errorCheck() {
        this.showError = true;
    }

    getTrackingData() {
        return {
            adId: this.adId,
            title: this.title,
            trackingCategory: this.trackingCategory,
            type: this.type,
            vertical: this.vertical,
            trackingObjectName: this.trackingObjectName,
        };
    }

    openPreviewModal() {
        const myEvent = new CustomEvent('open-modal', {
            composed: true,
            bubbles: true,
        });
        this.dispatchEvent(myEvent);
    }

    trackingSendMsg() {
        if (!this.loginId) {
            window.localStorage.setItem('showError', true);
        }
        trackButtonClick(this.getTrackingData());
    }

    _openVerificationModal() {
        this._eidModalOpen = true;
        const sidebar = document.getElementById('sexy-sidebar');
        if (sidebar) sidebar.style.zIndex = '20';
        const trackingData = this.getTrackingData();
        trackButtonClickUserMustVerify(trackingData);
        trackButtonClick(trackingData);
    }

    _handleModalClose() {
        this._eidModalOpen = false;
        const sidebar = document.getElementById('sexy-sidebar');
        if (sidebar) sidebar.style.zIndex = '3';
    }
}

customElements.define('finn-contact-button', FinnContactButton);
